import { useCallback, useEffect } from 'react';
import Tabs from '../../../../../generic/Tabs';
import Header from '../../../../../shared/settings/SettingsPageHeader';
import { useParams, useUnparsedQuery } from '../../../../../../contexts/RouterContext';
import { useTeamGroup } from '../../../../../../hooks/api/teams/teamGroups';
import Loader from '../../../../../generic/Loader';
import { IconText, RouterLink, icons } from '../../../../../generic';
import { useRoles } from '../../../../../../hooks/api';
import type { FullTeamGroupResponse } from '../../../../../../../shared/types/routes/teamGroups';

type Props = {
  isLoading: boolean;
  activeTab?: 'teams' | 'members';
  setActiveTab: (tab: 'teams' | 'members') => void;
  teamGroup: FullTeamGroupResponse | undefined;
  defaultRoleName: string
};

/**
 * TeamGroupManagement is the page for managing a team group.
 * It displays the assigned teams, as well as the members of the team group.
 * It allows for editing the team group name, as well as adding and removing teams from the group.
 */

const TeamGroupManagementComponent = ({ activeTab, setActiveTab, isLoading, teamGroup, defaultRoleName }: Props) => {
  return (
    <>
      <div className="margin-bottom-sm">
        <RouterLink to="/account/settings/organization/teams?tab=teamGroups">
          <IconText
            icon={icons.angleLeft}
            text="Back to Team Groups"
          />
        </RouterLink>
      </div>
      {isLoading || !teamGroup ? (
        <Loader />
      ) : (
        <>
          <Header heading={teamGroup.name} />
          <div>
            Default Role: {defaultRoleName}
          </div>
          <div />
          <div>
            <Tabs>
              <Tabs.Tab
                label="Assigned Teams"
                isActive={!activeTab || activeTab === 'teams'}
                onSelect={() => setActiveTab('teams')}
              />
              <Tabs.Tab
                label="Members"
                isActive={activeTab === 'members'}
                onSelect={() => setActiveTab('members')}
              />
            </Tabs>
          </div>
          <div className="teams-table">
            {activeTab === 'teams' ? (
              <h1>
                coming soon.... the teams table
              </h1>
            ) : (
              <h1>
                coming soon.... the members table
              </h1>
            )}
          </div>
        </>
      )}
    </>
  );
};

const TeamGroupManagement = () => {
  const { id } = useParams<{ id: number }>();
  const { data: teamGroup, isLoading: isLoadingTeam } = useTeamGroup(id);
  const { data: roles, isLoading: isLoadingRoles } = useRoles();
  const defaultRoleName = roles?.find((role) => role.id === teamGroup?.defaultRoleId)?.name ?? '...';
  const [query, setQuery] = useUnparsedQuery<{ tab?: 'teams' | 'members' }>();

  const setActiveTab = useCallback((tab: 'teams' | 'members') => {
    if (!tab || (tab !== 'teams' && tab !== 'members')) {
      setQuery({ tab: 'teams' });
    } else {
      setQuery({ tab });
    }
  }, [setQuery]);

  //  Ensure that there is always a tab set.
  useEffect(() => {
    if (!query?.tab) {
      setActiveTab('teams');
    }
  }, [query?.tab, setActiveTab]);

  return (
    <TeamGroupManagementComponent
      activeTab={query.tab}
      setActiveTab={setActiveTab}
      isLoading={isLoadingTeam || isLoadingRoles}
      teamGroup={teamGroup}
      defaultRoleName={defaultRoleName}
    />
  );
};

export default TeamGroupManagement;
